import React from 'react';
import { Helmet } from 'react-helmet-async';

type SEOProps = {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  structuredData?: object;
};

/**
 * SEO component for managing page-level meta tags and structured data
 * 
 * @example
 * <SEO 
 *   title="Moving Calculator - Get Instant Quotes"
 *   description="Calculate your moving costs instantly..."
 *   keywords="moving calculator, moving costs, moving quotes"
 *   canonical="/calculator"
 * />
 */
export const SEO: React.FC<SEOProps> = ({
  title = 'MovesOffer - Moving Company Auction Platform',
  description = 'Find the best moving companies with competitive auction-based pricing. Get quotes from verified movers for your next move.',
  keywords = 'moving companies, movers, moving quotes, auction, moving services, relocation',
  canonical,
  image = 'https://movesoffer.com/logo512.png',
  type = 'website',
  structuredData,
}) => {
  const fullTitle = title.includes('MovesOffer') ? title : `${title} | MovesOffer`;
  const canonicalUrl = canonical ? `https://movesoffer.com${canonical}` : 'https://movesoffer.com/';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="MovesOffer" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
